import { useState } from "react";

import { Dialog, DialogPopup } from "~/components/ui/dialog";
import { ProjectMilestoneDetailsView } from "./ProjectMilestoneDetailsView";
import { ProjectMilestoneOverview } from "./ProjectMilestoneOverview";
import type { ProjectMilestonesController } from "./useProjectMilestones";

export interface ProjectMilestoneManagerDialogProps {
  readonly open: boolean;
  readonly initialMilestoneId?: number | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly controller: ProjectMilestonesController;
}

/** Human-facing milestone creation and editing over canonical forge-context routes. */
export function ProjectMilestoneManagerDialog(props: ProjectMilestoneManagerDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {props.open ? (
        <ProjectMilestoneManagerBody
          key={props.initialMilestoneId ?? "overview"}
          initialMilestoneId={props.initialMilestoneId ?? null}
          controller={props.controller}
          onOpenChange={props.onOpenChange}
        />
      ) : null}
    </Dialog>
  );
}

interface ProjectMilestoneManagerBodyProps {
  readonly initialMilestoneId: number | null;
  readonly controller: ProjectMilestonesController;
  readonly onOpenChange: (open: boolean) => void;
}

function ProjectMilestoneManagerBody(props: ProjectMilestoneManagerBodyProps) {
  const [selectedMilestoneId, setSelectedMilestoneId] = useState<number | null>(
    props.initialMilestoneId,
  );
  const selectedMilestone =
    selectedMilestoneId == null
      ? null
      : (props.controller.milestones.find((milestone) => milestone.id === selectedMilestoneId) ??
        null);

  if (selectedMilestone != null) {
    return (
      <DialogPopup className="max-w-4xl max-sm:max-h-[calc(100dvh-1rem)]">
        <ProjectMilestoneDetailsView
          milestone={selectedMilestone}
          milestones={props.controller.milestones}
          mutating={props.controller.mutating}
          error={props.controller.error}
          onBack={() => setSelectedMilestoneId(null)}
          onSave={(input) => props.controller.update(selectedMilestone.id, input)}
          onDelete={async () => {
            const deleted = await props.controller.delete(selectedMilestone.id);
            if (deleted) setSelectedMilestoneId(null);
            return deleted;
          }}
        />
      </DialogPopup>
    );
  }

  return (
    <DialogPopup className="max-w-2xl max-sm:max-h-[calc(100dvh-1rem)]">
      <ProjectMilestoneOverview
        controller={props.controller}
        onClose={() => props.onOpenChange(false)}
        onSelectMilestone={setSelectedMilestoneId}
      />
    </DialogPopup>
  );
}
